/**
 * Codmon InputTextディレクティブ
 *
 * @description
 * このディレクティブは、テキスト入力とパスワード入力を扱う汎用的な入力フィールドを提供します。
 * サイズ、状態、ラベル、エラーメッセージなどをカスタマイズ可能です。
 *
 * @example
 * // 基本的な使い方（ラベル付き）
 * <codmon-input-text
 *   ng-model="email"
 *   type="text"
 *   label="メールアドレス"
 *   placeholder="例）example@example.com">
 * </codmon-input-text>
 *
 * @example
 * // 必須項目
 * <codmon-input-text
 *   ng-model="email"
 *   type="text"
 *   label="メールアドレス"
 *   placeholder="例）example@example.com"
 *   required="true">
 * </codmon-input-text>
 *
 * @example
 * // パスワード入力（表示/非表示トグル付き）
 * <codmon-input-text
 *   ng-model="password"
 *   type="password"
 *   label="パスワード">
 * </codmon-input-text>
 *
 * @example
 * // エラー表示
 * <codmon-input-text
 *   ng-model="email"
 *   type="text"
 *   label="メールアドレス"
 *   invalid="isEmailInvalid"
 *   error-message="メールアドレスの形式が正しくありません">
 * </codmon-input-text>
 *
 * @example
 * // Largeサイズ
 * <codmon-input-text
 *   ng-model="email"
 *   type="text"
 *   label="メールアドレス"
 *   size="large">
 * </codmon-input-text>
 *
 * @example
 * // サポートテキスト付き
 * <codmon-input-text
 *   ng-model="email"
 *   type="text"
 *   label="メールアドレス"
 *   support-text="登録したメールアドレスに確認メールが送信されます">
 * </codmon-input-text>
 *
 * @example
 * // ラベルなし
 * <codmon-input-text
 *   ng-model="email"
 *   type="text"
 *   placeholder="メールアドレスを入力">
 * </codmon-input-text>
 *
 * @example
 * // ラベル行の右端に任意の要素を置く（ヒントへのリンクなど）
 * // label が指定されている場合のみ描画される
 * <codmon-input-text
 *   ng-model="password"
 *   type="password"
 *   label="新しいパスワード">
 *   <codmon-input-text-label-trailing>
 *     <codmon-toggletip trigger-text="設定のヒント">ヒント本文</codmon-toggletip>
 *   </codmon-input-text-label-trailing>
 * </codmon-input-text>
 *
 * @property {string} type - 入力タイプ（text, password, または time）
 * @property {string} [label] - ラベルテキスト
 * @property {string} [placeholder] - プレースホルダーテキスト
 * @property {boolean} [required=false] - 必須項目かどうか（aria-required と「必須」バッジ）
 * @property {boolean} [hideRequiredTag=false] - 「必須」バッジだけを隠す（aria-required は維持する）
 * @property {string} [supportText] - サポートテキスト
 * @property {string} [errorMessage] - エラーメッセージ
 * @property {boolean} [invalid=false] - エラー状態を外部から制御
 * @property {string} [size=medium] - サイズ（medium または large）
 * @property {boolean} [ngDisabled=false] - 無効化状態
 * @property {string} [name] - input要素のname属性
 * @property {string} [autocomplete] - input要素のautocomplete属性
 * @property {boolean} [hideToggleOnAutofill=false] - オートフィルまたはペースト時にパスワードトグルボタンを非表示にするか
 *
 * @transclude labelTrailing - <codmon-input-text-label-trailing> でラベル行の右端に任意の要素を挿入できる（任意）
 */
angular.module('inspinia').directive('codmonInputText', () => {
  return {
    restrict: 'E',
    // `?` 付きの任意スロットなので、中身を持たない既存の呼び出しには影響しない
    transclude: {
      labelTrailing: '?codmonInputTextLabelTrailing',
    },
    scope: {
      type: '@',
      label: '@',
      placeholder: '@',
      required: '@',
      hideRequiredTag: '@',
      supportText: '@',
      errorMessage: '@',
      invalid: '=?',
      size: '@',
      ngDisabled: '=?',
      name: '@',
      autocomplete: '@',
      hideToggleOnAutofill: '@',
      testId: '@',
      ngModel: '=',
    },
    templateUrl: `/manage/src/view/directive/codmon-ui/codmon_input_text.html?${new Date().getTime()}`,
    link($scope, _element, _attrs) {
      // ユニークなIDを生成
      $scope.inputId = `codmon-input-text-${$scope.$id}`;
      $scope.errorMessageId = `codmon-input-text-error-${$scope.$id}`;
      $scope.supportTextId = `codmon-input-text-support-${$scope.$id}`;

      // デフォルト値の設定
      $scope.ngDisabled = !!$scope.ngDisabled;
      $scope.invalid = $scope.invalid === undefined ? false : !!$scope.invalid;
      $scope.isRequired = $scope.required === 'true';
      $scope.showRequiredTag = $scope.isRequired && $scope.hideRequiredTag !== 'true';

      const inputSizes = ['medium', 'large'];
      $scope.size = inputSizes.includes($scope.size) ? $scope.size : 'medium';

      const inputTypes = ['text', 'password', 'time'];
      $scope.type = inputTypes.includes($scope.type) ? $scope.type : 'text';

      // パスワード表示/非表示の状態
      $scope.isPasswordVisible = false;

      // オートフィル・ペースト状態
      $scope.isAutofilled = false;

      // hide-toggle-on-autofill が有効な場合のみオートフィル・ペースト検知を行う
      if ($scope.hideToggleOnAutofill === 'true') {
        const inputEl = _element[0].querySelector('input');
        if (inputEl) {
          // オートフィル検知（CSSアニメーションハック）
          const onAnimationStart = (event) => {
            if (event.animationName === 'onAutoFillStart') {
              $scope.$apply(() => {
                $scope.isAutofilled = true;
              });
            }
          };

          // ペースト時もトグルボタンを非表示にする
          const onPaste = () => {
            $scope.$apply(() => {
              $scope.isAutofilled = true;
            });
          };

          inputEl.addEventListener('animationstart', onAnimationStart);
          inputEl.addEventListener('paste', onPaste);

          $scope.$on('$destroy', () => {
            inputEl.removeEventListener('animationstart', onAnimationStart);
            inputEl.removeEventListener('paste', onPaste);
          });
        }

        // 値が空になったらオートフィル・ペースト状態をリセット
        $scope.$watch('ngModel', (newVal) => {
          if (!newVal) {
            $scope.isAutofilled = false;
          }
        });
      }

      // 入力タイプを動的に決定
      $scope.getInputType = () => {
        if ($scope.type === 'password') {
          // オートフィル時は強制的にpasswordにする
          if ($scope.isAutofilled) {
            return 'password';
          }
          // 通常時はisPasswordVisibleに応じて切り替え
          if (!$scope.isPasswordVisible) {
            return 'password';
          }
        }
        if ($scope.type === 'time') {
          return 'time';
        }
        return 'text';
      };

      // エラー状態
      $scope.hasError = () => {
        return !!$scope.invalid;
      };

      // aria-describedbyの値を計算
      $scope.getAriaDescribedBy = () => {
        const ids = [];
        if ($scope.hasError()) {
          ids.push($scope.errorMessageId);
        } else if ($scope.supportText) {
          ids.push($scope.supportTextId);
        }
        return ids.length > 0 ? ids.join(' ') : undefined;
      };

      // パスワード表示/非表示をトグル
      $scope.togglePasswordVisibility = () => {
        $scope.isPasswordVisible = !$scope.isPasswordVisible;
      };

      // コンテナのクラス
      $scope.containerClasses = () => {
        return {
          'codmon-input-text': true,
          [`codmon-input-text--${$scope.size}`]: true,
        };
      };

      // 入力ラッパーのクラス
      $scope.inputWrapperClasses = () => {
        return {
          'codmon-input-text__wrapper': true,
          'codmon-input-text__wrapper--error': $scope.hasError(),
          'codmon-input-text__wrapper--disabled': $scope.ngDisabled,
        };
      };
    },
  };
});
